import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_SYMS = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'VIX'];

export default function IntradayPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [symbol, setSymbol] = useState(searchParams.get('symbol') || 'NIFTY');
  const [date, setDate] = useState(searchParams.get('date') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async (sym, dt) => {
    setLoading(true);
    setError(null);
    try {
      let url = `/api/api_intraday.php?symbol=${encodeURIComponent(sym)}`;
      if (dt) url += `&date=${encodeURIComponent(dt)}`;
      const res = await fetch(url, { credentials: 'include' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      setData(json);
      if (!dt && json.date) setDate(json.date);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(symbol, date);
  }, [symbol, date]);

  const onSymbol = (s) => {
    setSymbol(s);
    setSearchParams({ symbol: s, ...(date ? { date } : {}) });
  };

  const prices = data?.prices || {};
  const series = data?.series || [];

  return (
    <div className="obsidian-content">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-xs uppercase text-slate-400">Intraday • 5m OHLC</span>
          <h1 className="text-2xl font-semibold tracking-tight">{symbol} — {data?.date || '...'}</h1>
        </div>
        <div className="flex gap-2">
          {DEFAULT_SYMS.map(s => (
            <button key={s} onClick={() => onSymbol(s)} className={`px-3 py-1 rounded text-sm border ${symbol === s ? 'bg-emerald-500 text-black border-emerald-400' : 'border-slate-700 hover:bg-slate-800'}`}>{s}</button>
          ))}
        </div>
      </div>

      {loading && <div className="text-slate-400">Loading intraday data… (may take a few seconds on first load)</div>}
      {error && <div className="text-red-400">Error: {error}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {Object.keys(prices).slice(0, 8).map(sym => {
              const p = prices[sym] || {};
              return (
                <div key={sym} className="tmr-stat-card">
                  <div className="tmr-stat-label">{sym}</div>
                  <div className="tmr-stat-value">{p.last_price ?? p.close ?? '—'}</div>
                  <div className="text-[10px] text-slate-400">H {p.day_high ?? '—'} / L {p.day_low ?? '—'}</div>
                </div>
              );
            })}
          </div>

          <div className="tmr-card p-3 mb-4">
            <div className="flex justify-between text-sm mb-2">
              <div className="font-semibold">Candles ({series.length})</div>
              <div className="text-xs text-slate-400">Source: ohlc_5min • React migration</div>
            </div>
            <div className="overflow-auto max-h-[420px] text-xs">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-left bg-slate-900">
                    <th className="p-1 border border-slate-800">Time</th>
                    <th className="p-1 border border-slate-800">O</th>
                    <th className="p-1 border border-slate-800">H</th>
                    <th className="p-1 border border-slate-800">L</th>
                    <th className="p-1 border border-slate-800">C</th>
                    <th className="p-1 border border-slate-800">Vol</th>
                    <th className="p-1 border border-slate-800">RSI</th>
                  </tr>
                </thead>
                <tbody>
                  {series.slice().reverse().slice(0, 60).map((c, i) => (
                    <tr key={i} className="border-b border-slate-800 hover:bg-slate-900/60">
                      <td className="p-1 font-mono">{c.time}</td>
                      <td className="p-1">{Number(c.o).toFixed(2)}</td>
                      <td className="p-1">{Number(c.h).toFixed(2)}</td>
                      <td className="p-1">{Number(c.l).toFixed(2)}</td>
                      <td className="p-1 font-semibold">{Number(c.c).toFixed(2)}</td>
                      <td className="p-1">{c.v ? Math.round(c.v) : '—'}</td>
                      <td className="p-1">{c.rsi ? Number(c.rsi).toFixed(1) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-amber-400 mt-2">Note: Full interactive 200EMA + FIB + mini-charts port coming in next iteration. Current uses lightweight data from existing engine.</div>
          </div>

          {data.signals && data.signals.length > 0 && (
            <div className="tmr-card p-3">
              <div className="font-semibold mb-1">Signals</div>
              <div className="text-xs text-slate-300 max-h-40 overflow-auto">
                {JSON.stringify(data.signals).slice(0, 500)}...
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
